import React, { useState, useCallback, useContext, useEffect } from "react"
import { useHistory, Switch, Route, RouteComponentProps } from "react-router-dom"

import { AuthContext } from "@izettle/react-spa"

import { Error } from "@/components/error"
import { baseUrl } from "@/constants"
import { validateFileFormat } from "@/helpers"
import { SelectFilesProps } from "@/models"
import { usePurposes } from "@/providers/purposes"
import { uploadDocument } from "@/services"
import {
  ViewedPurposeEvent,
  SelectedUploadMethodEvent,
  SubmittedPhotoEvent,
  sendToTrackingService,
} from "@/tracking"

import { FlipYourCard } from "./flip-your-card"
import { Overview } from "./overview"
import { Upload } from "./upload"

export const ProofOfIdentityView: React.FC<RouteComponentProps> = ({ match }) => {
  const [file, setFile] = useState<File>()
  const [purpose, setPurpose] = useState<string>("")
  const [type, setType] = useState<string>("")
  const [description, setDescription] = useState<string>("")
  const [isDoubleSided, setIsDoubleSided] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [successfullySubmittedToBackend, setSuccessfullySubmittedToBackend] =
    useState<boolean>(false)
  let timer: ReturnType<typeof setTimeout>
  const { checkValidPurpose } = usePurposes()

  const history = useHistory()

  useEffect(() => {
    if (!successfullySubmittedToBackend) return
    return () => clearTimeout(timer)
  }, [successfullySubmittedToBackend])

  const selectFiles: SelectFilesProps = useCallback(
    (
      selectedFiles,
      selectedPurpose,
      selectedType,
      selectedDescription,
      redirect,
      selectedIsDoubleSided,
    ) => {
      setSuccessfullySubmittedToBackend(false)
      if (!validateFileFormat(selectedFiles)) {
        history.push(`${baseUrl}/proofOfIdentity/error/invalidFileFormat`)
        return Error
      }

      if (selectedFiles.length) {
        setFile(selectedFiles[0])
      }

      setPurpose(selectedPurpose ?? "")
      setType(selectedType ?? "")
      setDescription(selectedDescription ?? "")
      setIsDoubleSided(selectedIsDoubleSided ?? false)

      const payload = {
        purpose: "proofOfIdentity",
        documentType: selectedType,
        uploadMethod: "photoLibrary",
      }
      sendToTrackingService({ ...SelectedUploadMethodEvent, payload })

      if (redirect) {
        history.push(redirect)
      }
    },
    [file],
  )

  const { getToken } = useContext(AuthContext)

  const upload = useCallback(
    async (redirect: string) => {
      setIsUploading(true)
      setSuccessfullySubmittedToBackend(false)

      try {
        await uploadDocument(getToken, file, purpose, type, description)
        setIsUploading(false)
        const payload = {
          purpose: "proofOfIdentity",
          documentType: type,
        }
        const page = "DocumentQualityCheck"
        sendToTrackingService({ ...SubmittedPhotoEvent, page, payload })
        setSuccessfullySubmittedToBackend(true)
        timer = setTimeout(() => history.push(redirect), 2000)
      } catch {
        setIsUploading(false)
        setSuccessfullySubmittedToBackend(false)
        history.push(`${baseUrl}/proofOfIdentity/error`)
      }
    },
    [file],
  )

  const checkIfPageIsValid = async () => {
    const isValid = await checkValidPurpose("proofOfIdentity")
    if (!isValid) {
      history.replace(baseUrl)
      return
    }
    const payload = {
      purpose: "proofOfIdentity",
    }
    sendToTrackingService({ ...ViewedPurposeEvent, payload })
  }

  useEffect(() => {
    void checkIfPageIsValid()
  }, [])

  return (
    <Switch>
      <Route
        exact
        path={`${match.path}`}
        render={(props) => <Overview {...props} selectFiles={selectFiles} />}
      />
      <Route
        path={`${match.path}/flip-your-card`}
        render={() => (
          <FlipYourCard isDoubleSided={isDoubleSided} selectFiles={selectFiles} />
        )}
      />
      <Route
        path={`${match.path}/upload-front`}
        render={() => (
          <Upload
            file={file}
            isUploading={isUploading}
            successfullySubmittedToBackend={successfullySubmittedToBackend}
            redirect={`${baseUrl}/proofOfIdentity/flip-your-card`}
            path="upload-front"
            upload={upload}
          />
        )}
      />
      <Route
        path={`${match.path}/upload-back`}
        render={() => (
          <Upload
            file={file}
            isUploading={isUploading}
            successfullySubmittedToBackend={successfullySubmittedToBackend}
            redirect={baseUrl}
            path="upload-back"
            upload={upload}
            
          />
        )}
      />
      <Route
        path={`${match.path}/upload`}
        render={() => (
          <Upload
            file={file}
            isUploading={isUploading}
            successfullySubmittedToBackend={successfullySubmittedToBackend}
            redirect={baseUrl}
            path="upload"
            upload={upload}
          />
        )}
      />
      <Route
        path={`${match.path}/error/:errorType?`}
        render={(props) => (
          <Error
            cancelUrl={`${baseUrl}/proofOfIdentity`}
            errorType={props.match.params.errorType}
          />
        )}
      />
    </Switch>
  )
}
